import { useEffect, useRef, useState } from 'react';
import { spacingClass } from '@/utils/spacing';
import { SlideMedia } from './SlideMedia';

const TEXT_ALIGN_CLASS = {
  left: 'text-left items-start',
  center: 'text-center items-center',
  right: 'text-right items-end',
};

/**
 * Vetrina a scroll: pannello immagine quadrato fisso (sticky) mentre il testo
 * di ogni slide scorre normalmente accanto — l'immagine cambia in crossfade
 * in base a quale slide di testo è "al centro" durante lo scroll (nessuna
 * animazione sul testo, scorre in flusso normale).
 * Nessuno scrim qui (a differenza degli altri blocchi a slide): titolo/testo
 * stanno già a fianco del pannello media, non sopra — `showOverlay` per
 * slide controlla solo se quel titolo/testo compaiono nella colonna,
 * stesso significato ("con o senza didascalia") ma senza oscuramento da
 * nascondere insieme, semplicemente perché qui non esiste.
 * @param {{
 *   slides?: Array<{ mediaType?: 'image'|'video', image?: string, imageAlt?: string, videoUrl?: string, showOverlay?: boolean, title?: string, text?: string }>,
 *   imageSide?: 'left' | 'right', textAlign?: 'left' | 'center' | 'right',
 *   spacing?: string,
 * }} props
 */
export function ScrollShowcaseBlock({ slides = [], imageSide = 'left', textAlign = 'left', spacing = 'normal' }) {
  const [active, setActive] = useState(0);
  const slideRefs = useRef([]);
  slideRefs.current = [];

  function registerSlideRef(el, i) {
    if (el) slideRefs.current[i] = el;
  }

  // Slide "attiva" = quella che sta attraversando il centro del viewport —
  // IntersectionObserver con rootMargin negativo sopra/sotto (il trucco
  // standard per rilevare "chi sta al centro ora").
  useEffect(() => {
    if (slides.length < 2) return undefined;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          const i = slideRefs.current.indexOf(entry.target);
          if (i !== -1) setActive(i);
        });
      },
      { rootMargin: '-45% 0px -45% 0px', threshold: 0 }
    );
    slideRefs.current.forEach((el) => el && observer.observe(el));
    return () => observer.disconnect();
  }, [slides.length]);

  function goTo(i) {
    slideRefs.current[i]?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }

  if (slides.length === 0) return null;

  return (
    <section className={'px-6 md:px-16 ' + spacingClass(spacing)}>
      <div
        className={
          'max-w-6xl mx-auto flex flex-col gap-10 md:gap-16 ' +
          (imageSide === 'right' ? 'md:flex-row-reverse' : 'md:flex-row')
        }
      >
        {/* Centratura via flexbox nativo dentro il box sticky, non
            translateY(-50%): un transform si applica sempre, anche quando il
            pannello non è ancora "agganciato" (in flusso normale, prima che
            lo sticky scatti) — spostandolo in alto e sovrapponendolo al
            blocco precedente durante l'ingresso in vista. h-[calc(100vh-Xrem)]
            + top-[Xrem] compensano l'header fisso (~4.5rem da scrollato). */}
        <div className="md:w-1/2 md:shrink-0 md:sticky md:top-[4.5rem] md:h-[calc(100vh-4.5rem)] md:flex md:items-center">
          <div className="relative w-full max-w-[560px] mx-auto md:mx-0 aspect-square rounded-[28px] overflow-hidden bg-theme-text">
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_100%,color-mix(in_srgb,var(--color-accent)_55%,transparent),transparent_65%)]" />
            {slides.map((slide, i) => (
              <div key={i} className="absolute inset-0 transition-opacity duration-700 ease-in-out" style={{ opacity: i === active ? 1 : 0 }}>
                <SlideMedia
                  mediaType={slide.mediaType}
                  image={slide.image}
                  imageAlt={slide.imageAlt}
                  videoUrl={slide.videoUrl}
                  fallbackAlt={slide.title}
                />
              </div>
            ))}
            {slides.length > 1 && (
              <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex">
                {slides.map((_, i) => (
                  // Pallino visibile piccolo, area cliccabile più grande (stesso
                  // pattern di HeroSlideshowBlock) — porta alla slide di testo
                  // corrispondente, l'osservatore aggiorna "active" da solo.
                  <button
                    key={i}
                    onClick={() => goTo(i)}
                    aria-label={`Vai alla slide ${i + 1}`}
                    className="flex items-center justify-center p-2.5"
                  >
                    <span
                      className="block w-1.5 h-1.5 rounded-full transition-colors"
                      style={{ background: i === active ? '#fff' : 'rgba(255,255,255,0.4)' }}
                    />
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="md:w-1/2 flex flex-col">
          {slides.map((slide, i) => (
            <div
              key={i}
              ref={(el) => registerSlideRef(el, i)}
              className={'flex flex-col justify-center min-h-[50vh] md:min-h-screen gap-4 ' + TEXT_ALIGN_CLASS[textAlign]}
            >
              {slide.showOverlay !== false && slide.title && (
                <h2 className="font-heading text-h2 font-light text-theme-text">{slide.title}</h2>
              )}
              {slide.showOverlay !== false && slide.text && <p className="text-body-lg text-theme-text-muted leading-relaxed max-w-md">{slide.text}</p>}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
